import api from '@/api/axiosApi'
import type { PaginationMeta, ExportRead } from '@/types'
import type { CreateExportResponse } from '@/hooks/useExport'

// --- Types ---

export interface UserSummary {
  id: string
  email?: string | null
  display_name?: string | null
}

export type SubmissionStatus =
  | 'PENDING'
  | 'APPROVED'
  | 'REJECTED'
  | 'WITHDRAWN'
  | 'SENT_BACK'
  | 'UNAPPROVED'

export interface ManagerStatsResponse {
  new_count: number
  pending_count: number
  sent_back_count: number
}

export interface ManagerSubmissionSummary {
  submission_id: string
  plan_id: string
  plan_name: string
  version_id: string
  submitted_by: UserSummary | null
  submitted_at: string
  plan_status: string
  submission_status: SubmissionStatus
  seen_by_approver: boolean
}

export interface PaginatedManagerSubmissions {
  data: ManagerSubmissionSummary[]
  pagination: PaginationMeta
}

export interface ManagerSubmissionsFilters {
  q?: string
  status?: string
  page?: number
  pageSize?: number
}

export interface ManagerExportRequest {
  columns?: string[] | null
  sort?: string | null
  status?: string[] | null
}

// --- API Functions ---

export async function fetchManagerStats(): Promise<ManagerStatsResponse> {
  const response = await api.get<ManagerStatsResponse>('/manager/stats')
  return response.data
}

export async function fetchManagerSubmissions(
  filters: ManagerSubmissionsFilters = {}
): Promise<PaginatedManagerSubmissions> {
  const { q, status, page = 1, pageSize = 20 } = filters

  const offset = (page - 1) * pageSize
  const params: Record<string, string | number> = {
    offset,
    limit: pageSize,
    sort: '-submitted_at',
  }

  if (q && q.trim() !== '') {
    params.q = q.trim()
  }

  if (status && status !== 'all') {
    params.status = status
  }

  const response = await api.get<PaginatedManagerSubmissions>(
    '/manager/submissions',
    { params }
  )
  return response.data
}

export async function approveSubmission(submissionId: string): Promise<void> {
  await api.post(`/manager/submissions/${submissionId}/approve`, null)
}

export async function sendBackSubmission(submissionId: string): Promise<void> {
  await api.post(`/manager/submissions/${submissionId}/send-back`, null)
}

export async function markSubmissionsSeen(
  submissionIds: string[]
): Promise<void> {
  await api.post('/manager/submissions/mark-seen', {
    submission_ids: submissionIds,
  })
}

export async function createManagerExport(
  request?: ManagerExportRequest
): Promise<CreateExportResponse> {
  const response = await api.post<ExportRead>('/manager/exports', request ?? {})
  const data = response.data
  return {
    id: data.id,
    processing_id: data.processing_id ?? '',
    status: 'PENDING',
  }
}

export interface UnapproveSubmissionBody {
  decision_notes?: string | null
}

export async function unapproveSubmission(
  submissionId: string,
  body?: UnapproveSubmissionBody
): Promise<void> {
  await api.post(`/manager/submissions/${submissionId}/unapprove`, body ?? {})
}

export async function getManagerExport(exportId: string): Promise<ExportRead> {
  const response = await api.get<ExportRead>(`/manager/exports/${exportId}`)
  return response.data
}
